import { useCallback, useEffect } from 'react'
import L from 'leaflet'
import type { Position } from '../../../../types'
import type { POIElement, PinnedElement } from '../types'

interface UseOverlayPositionsProps {
  mapRef: React.MutableRefObject<L.Map | null>
  poiOverlayRef: React.MutableRefObject<HTMLDivElement | null>
  poiElementsRef: React.MutableRefObject<Map<string, POIElement>>
  pinnedElementsRef: React.MutableRefObject<Map<string, PinnedElement>>
  userMarkerElementRef: React.MutableRefObject<HTMLDivElement | null>
  isInitializedRef: React.MutableRefObject<boolean>
  position: Position | null
}

export const useOverlayPositions = ({
  mapRef,
  poiOverlayRef,
  poiElementsRef,
  pinnedElementsRef,
  userMarkerElementRef,
  isInitializedRef,
  position,
}: UseOverlayPositionsProps) => {
  // Update overlay positions
  const updateOverlayPositions = useCallback(() => {
    if (!poiOverlayRef.current || !mapRef.current) return

    // Verify map is in a valid state
    try {
      if (!mapRef.current.getContainer() || !mapRef.current.getSize()) {
        return
      }
    } catch (error) {
      return
    }

    const map = mapRef.current

    // Update POI markers
    poiElementsRef.current.forEach((poiData) => {
      try {
        if (!poiData.element.isConnected) return
        const point = map.latLngToContainerPoint([poiData.lat, poiData.lon])
        if (point && typeof point.x === 'number' && typeof point.y === 'number') {
          poiData.element.style.transform = `translate(${point.x}px, ${point.y}px)`
          poiData.element.style.display = 'block'
        } else {
          poiData.element.style.display = 'none'
        }
      } catch (error) {
        // Ignore errors for individual markers
      }
    })

    // Update user marker
    if (userMarkerElementRef.current && position) {
      try {
        if (userMarkerElementRef.current.isConnected) {
          const point = map.latLngToContainerPoint([position.lat, position.lon])
          if (point && typeof point.x === 'number' && typeof point.y === 'number') {
            userMarkerElementRef.current.style.transform = `translate(${point.x}px, ${point.y}px)`
            userMarkerElementRef.current.style.display = 'block'
          } else {
            userMarkerElementRef.current.style.display = 'none'
          }
        }
      } catch (error) {
        // Ignore errors for user marker
      }
    }

    // Update pinned location markers
    pinnedElementsRef.current.forEach((pinData) => {
      try {
        if (!pinData.element.isConnected) return
        const point = map.latLngToContainerPoint([pinData.lat, pinData.lon])
        if (point && typeof point.x === 'number' && typeof point.y === 'number') {
          pinData.element.style.transform = `translate(${point.x}px, ${point.y}px)`
          pinData.element.style.display = 'block'
        } else {
          pinData.element.style.display = 'none'
        }
      } catch (error) {
        // Ignore errors for individual pins
      }
    })
  }, [position, mapRef, poiOverlayRef, poiElementsRef, pinnedElementsRef, userMarkerElementRef])

  // Listen to map move/zoom events - use requestAnimationFrame for smooth updates
  useEffect(() => {
    if (!mapRef.current || !isInitializedRef.current) return

    const map = mapRef.current
    let animationFrameId: number | null = null
    let isUpdateScheduled = false

    const handleMapMove = () => {
      // If update already scheduled, skip
      if (isUpdateScheduled) return
      
      // Cancel any pending animation frame to avoid duplicate updates
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      
      isUpdateScheduled = true
      
      // Schedule update for next animation frame for smooth 60fps updates
      animationFrameId = requestAnimationFrame(() => {
        updateOverlayPositions()
        animationFrameId = null
        isUpdateScheduled = false
      })
    }
    
    // Also update immediately on certain events for better responsiveness
    const handleMapMoveImmediate = () => {
      updateOverlayPositions()
    }

    // Listen to all movement events for realtime updates
    map.on('move', handleMapMove)
    map.on('drag', handleMapMove)
    map.on('zoom', handleMapMove)
    map.on('zoomstart', handleMapMoveImmediate) // Immediate for zoom start
    map.on('zoomend', handleMapMoveImmediate) // Immediate for zoom end
    map.on('movestart', handleMapMoveImmediate) // Immediate when movement starts
    map.on('moveend', handleMapMoveImmediate) // Immediate when movement ends
    map.on('dragstart', handleMapMoveImmediate) // Immediate when drag starts
    map.on('dragend', handleMapMoveImmediate) // Immediate when drag ends

    // Initial position update
    handleMapMoveImmediate()

    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      map.off('move', handleMapMove)
      map.off('drag', handleMapMove)
      map.off('zoom', handleMapMove)
      map.off('zoomstart', handleMapMoveImmediate)
      map.off('zoomend', handleMapMoveImmediate)
      map.off('movestart', handleMapMoveImmediate)
      map.off('moveend', handleMapMoveImmediate)
      map.off('dragstart', handleMapMoveImmediate)
      map.off('dragend', handleMapMoveImmediate)
    }
  }, [updateOverlayPositions, mapRef, isInitializedRef])

  return { updateOverlayPositions }
}
